import FileSaver from 'file-saver';
import i18next, { TFunction } from 'i18next';
import DataStore, { Query, QueryResult } from 'src/core/stores/data-store';
import FormStore from '../core/stores/form-store';
import { CommandResult, Message } from '../core/stores/types';
import i18n, { Languages } from '../i18n';
import moment from 'moment';
import { IdentityType, ScopeType } from 'src/common/types';
import { CsvReportKeys as IdentityCsvReportKeys } from './users-store';
import { CreateGroupItem, GroupItem, GroupType } from './groups-store';

export interface LicenseDataSummaryItem {
  id: string;
  toolCode: string;
  deviceType: DeviceType;
  fromDate: Date;
  untilDate: Date;
  createdOn: Date;
  createdBy: string;
  customer?: string;
  project?: string;
  organizationId: string;
  organizationName?: string;
  signOffPending: boolean;
  unlimited: boolean;
  isRecurrent?: boolean;
  workingHours: number;
}
export enum DeviceType {
  GTD = 'GTD',
  GLC = 'GLC',
  SGTD = 'SGTD',
  MCTD = 'MCTD',
  MCTD2 = 'MCTD2',
  Aligner = 'Aligner',
  CMTD = 'CMTD',
  SCTD = 'SCTD',
  Oprim = 'Oprim',
}

export enum CoreDiameter {
  B = 0,
  N = 1,
  H = 2,
}

export const deviceTypeSorter = (a: DeviceType, b: DeviceType) => {
  const deviceTypeOrder = [
    DeviceType.GTD,
    DeviceType.MCTD2,
    DeviceType.MCTD,
    DeviceType.Aligner,
    DeviceType.CMTD,
    DeviceType.SCTD,
    DeviceType.GLC,
    DeviceType.SGTD,
    DeviceType.Oprim,
  ];

  return deviceTypeOrder.indexOf(a) - deviceTypeOrder.indexOf(b);
};

export const deviceTypeLabels: Record<DeviceType, string> = {
  Aligner: 'Aligner / Polestar',
  CMTD: 'Coremaster / CoreLite',
  GLC: 'Gyrologic / GyroScout',
  GTD: 'Gyromaster / Gyrotracer / Gyromaster 2.0',
  MCTD: 'Magcruiser',
  MCTD2: 'Magcruiser 2.0',
  SCTD: 'SmartCore',
  SGTD: 'SGTD',
  Oprim: 'OprimCore',
};

export const hourBasedLicenses = [DeviceType.MCTD];

export const isHourBasedLicense = (deviceType: DeviceType | undefined) => {
  return deviceType ? hourBasedLicenses.includes(deviceType) : true;
};

export const licenseStarted = (fromDate: Date, deviceType?: DeviceType) => {
  const now = moment(new Date());
  if (!isHourBasedLicense(deviceType)) now.add(now.utcOffset(), 'minutes');
  return now.isAfter(moment(fromDate));
};

export const licenseEnded = (untilDate: Date, deviceType?: DeviceType) => {
  const now = moment(new Date());
  if (!isHourBasedLicense(deviceType)) now.add(now.utcOffset(), 'minutes');
  return moment(now).isAfter(moment(untilDate));
};

export const licenseIsEnabled = (fromDate: Date, untilDate?: Date, deviceType?: DeviceType) => {
  if (!untilDate) return true;
  return licenseStarted(fromDate, deviceType) ? moment(untilDate).isAfter(moment(new Date())) : false;
};

export const updateLicenseFormat = <T extends LicenseDataSummaryItem | LicenseDataItem>(license: T) => {
  if (license.deviceType === DeviceType.SCTD) {
    const newToolCode = license.toolCode.padStart(5, '0');
    return { ...license, toolCode: newToolCode };
  } else {
    return license;
  }
};

export const getLicenseToolCode = <T extends LicenseDataSummaryItem | LicenseDataItem>(license: T) => {
  if (license.deviceType === DeviceType.SCTD) {
    return license.toolCode.slice(1);
  } else {
    return license.toolCode;
  }
};

export const getSmartCoreLicenseToolDiameter = <T extends LicenseDataSummaryItem | LicenseDataItem>(license: T) => {
  const label = Object.entries(CoreDiameter).find(([key, value]) => isNaN(parseInt(key)) && value === parseInt(license.toolCode[0]));
  return label ? label[0] : 'B';
};

export interface CreateLicenseDataItem {
  toolCode: string;
  deviceType: DeviceType;
  fromDate?: Date;
  untilDate?: Date;
  workingHours?: number;
  unlimited: boolean;
  notes: string;
  customer?: string;
  project?: string;
  organizationName?: string;
  organizationId?: string;
  language?: string;
  isRecurrent?: boolean;
}
export interface LicenseDataItem {
  id: string;
  toolCode: string;
  deviceType: DeviceType;
  fromDate?: Date;
  untilDate?: Date;
  workingHours?: number;
  unlimited: boolean;
  notes: string;
  customer?: string;
  project?: string;
  customers: string[];
  projects: string[];
  needsSignOff: boolean;
  signedOffByUserName?: string;
  signedOffOn?: Date;
  canCreateUnlimitedLicense: boolean;
  isRecurrent?: boolean;
}
export interface LicenseDataSummaryCsvRequest extends LicenseDataSummaryItem {
  yesTranslation: string;
  noTranslation: string;
  activeDays: number;
  status: string;
}
export interface LicenseSuggestions {
  customers: string[];
  projects: string[];
}

export interface LicenseSuggestionsRequest {
  organizationId: string | undefined;
}

export interface StructmasterLicenseSummary {
  createdBy: string;
  id: string;
  roleId: string;
  role: string;
  scopeId: string;
  scopeTitle: string;
  scopeType: ScopeType;
  identityId: string; // UserId or GroupId
  identityTitle: string;
  identityType: IdentityType;
  expirationDate?: Date;
  activeUser: string;
  softwareVersion: string;
  creationDate: Date;
  country: string;
  createdByName: string;
  organizationName: string;
  licenseName: string;
  status: 'Active' | 'NotActive' | 'Pending';
  activeDays: number;
}

export interface StructmasterLicenseItem {
  id: string;
  organizationName: string;
  licenseName: string;
  organizationId: string;
  expirationDate: Date;
  members: {
    memberId: string;
    name: string;
    idOrEmail: string;
  }[];
  activeDays: number;
  isActive: boolean;
}

export interface UnsignedStructmasterLicense {
  id?: string;
  organizationName: string;
  licenseName: string;
  organizationId: string;
  members: {
    memberId: string;
    idOrEmail: string;
    name: string;
  }[];
  expirationDate: Date;
}

export interface UpdateUnsignedStructmasterLicense {
  name: string;
  expirationDate: Date;
  organizationId: string;
}

export interface CreateUnsignedStructmasterLicense {
  id?: string;
  name: string;
  organizationId: string;
  members: string[];
  roleExpirationDate: Date;
}

export interface CreateStructmasterLicenseItem {
  createGroupBody: CreateGroupItem;
  roleId: string;
  expirationDate: Date;
}

export enum CsvReportKeys {
  ToolCode,

  DeviceType,

  FromDate,

  UntilDate,

  CreatedOn,

  CreatedBy,

  ActiveDays,

  Customer,

  Project,

  OrganizationName,

  Country,

  Status,

  SignOffPending,

  LogType,

  UserName,

  Description,

  WorkingHours,

  ToolDiameter,

  IsRecurrent,
}

export interface reportHeader {
  key: CsvReportKeys;

  translatedHeader: string;
}

export interface StructmasterReportHeader {
  key: IdentityCsvReportKeys;

  translatedHeader: string;
}
export const getStructmasterTranslatedHeader = (key: IdentityCsvReportKeys, t: TFunction) => {
  switch (key) {
    case IdentityCsvReportKeys.ActiveDays:
      return t('ActiveDays');
    case IdentityCsvReportKeys.CreatedBy:
      return t('CreatedBy');
    case IdentityCsvReportKeys.Country:
      return t('Country');
    case IdentityCsvReportKeys.OrganizationName:
      return t('OrganizationName');
    case IdentityCsvReportKeys.DisplayName:
      return t('DisplayName');
    case IdentityCsvReportKeys.FromDate:
      return t('FromDate');
    case IdentityCsvReportKeys.UntilDate:
      return t('UntilDate');
    case IdentityCsvReportKeys.LicenseName:
      return t('LicenseName');
    case IdentityCsvReportKeys.Status:
      return t('Status');
    case IdentityCsvReportKeys.ActiveUser:
      return t('ActiveUser');
    case IdentityCsvReportKeys.SoftwareVersion:
      return t('SoftwareVersion');
  }
};
export const getTranslatedHeader = (key: CsvReportKeys, t: TFunction) => {
  switch (key) {
    case CsvReportKeys.ToolCode:
      return t('ToolCode');
    case CsvReportKeys.ToolDiameter:
      return t('ToolDiameter');
    case CsvReportKeys.DeviceType:
      return t('DeviceType');
    case CsvReportKeys.FromDate:
      return t('FromDate');
    case CsvReportKeys.UntilDate:
      return t('UntilDate');
    case CsvReportKeys.WorkingHours:
      return t('WorkingHours');
    case CsvReportKeys.CreatedOn:
      return t('CreatedOn');
    case CsvReportKeys.CreatedBy:
      return t('CreatedBy');
    case CsvReportKeys.Customer:
      return t('Customer');
    case CsvReportKeys.Project:
      return t('Project');
    case CsvReportKeys.OrganizationName:
      return t('Organization');
    case CsvReportKeys.SignOffPending:
      return t('SignOffPending');
    case CsvReportKeys.ActiveDays:
      return t('ActiveDays');
    case CsvReportKeys.Status:
      return t('Status');
    case CsvReportKeys.IsRecurrent:
      return t('Recurrence');
    case CsvReportKeys.Country:
      return t('Country');
    default:
      return t('ToolCode');
  }
};
export interface CsvReportRequest {
  csvSeparator: string;

  headers: reportHeader[];

  currentLanguage: string;

  fileName: string;

  organizationIds?: string[];

  organizationCountryPairs: { country: string; organizationId: string }[];

  yesTranslation: string;

  noTranslation: string;

  activeTranslation: string;

  notActiveTranslation: string;
}

export interface StructmasterCsvReportRequest {
  csvSeparator: string;

  headers: StructmasterReportHeader[];

  currentLanguage: string;

  fileName: string;

  organizationIds?: string[];

  organizationCountryPairs: { country: string; organizationId: string }[];

  minutesDifference: number;

  yesTranslation: string;

  noTranslation: string;

  activeTranslation: string;

  notActiveTranslation: string;

  userId?: string;
}

export interface NewLicensCreationAttributes {
  maxNumberOfLicensesPerMonth: number;

  maxLicensesPeriodInDays: number;

  maximumLicenseDueDate: Date;

  allowedDevices: DeviceType[];

  canCreateUnlimitedLicense: boolean;

  needsSignOff: boolean;

  identityId: string;

  identityType: IdentityType;

  scopeId: string;

  canCreateRecurrentLicenses: boolean;
}
export interface LicenseCreationAttributes {
  id: string;

  maxNumberOfLicensesPerMonth: number;

  maxNumberOfLicensesInCurrentMonth: number;

  maxLicensesPeriodInDays: number;

  maximumLicenseDueDate: Date;

  allowedDevices: DeviceType[];

  needsSignOff: boolean;

  canCreateUnlimitedLicense: boolean;

  identityId: string;

  identityType: IdentityType;

  scopeId: string;

  canCreateRecurrentLicenses: boolean;
}
export interface LicenseManagerAssignmentAttributes {
  maxNumberOfLicensesPerMonth: number;

  maxLicensesPeriodInDays: number;

  maximumLicenseDueDate: Date;

  roleExpirationDate: Date;

  allowedDevices: DeviceType[];

  canCreateUnlimitedLicense: boolean;

  needsSignOff: boolean;

  canCreateRecurrentLicenses: boolean;
}
export interface LicenseScopeRequest {
  identityId: string;
  scopeId: string;
  identityType: IdentityType;
}

export interface StructmasterLicensesQueryFilter {
  query: Partial<Query>;
  ids?: string[];
  userId?: string;
  unsigned?: boolean;
}

export const dateDiffToHours = (from: Date, until: Date) => {
  const parsedFrom = new Date(from);
  const parsedUntil = new Date(until);
  return Math.abs(parsedUntil.getTime() - parsedFrom.getTime()) / 36e5;
};

export const hoursToDateDiff = (hours: number) => {
  const from = new Date();
  const until = new Date(new Date().setTime(from.getTime() + hours * 36e5));
  return { from, until };
};

export const getLicenseTableHeaders = (t: TFunction) => {
  return [
    {
      key: CsvReportKeys.ToolCode,

      translatedHeader: getTranslatedHeader(CsvReportKeys.ToolCode, t),
    },
    {
      key: CsvReportKeys.ToolDiameter,

      translatedHeader: getTranslatedHeader(CsvReportKeys.ToolDiameter, t),
    },
    {
      key: CsvReportKeys.DeviceType,

      translatedHeader: getTranslatedHeader(CsvReportKeys.DeviceType, t),
    },
    {
      key: CsvReportKeys.FromDate,

      translatedHeader: getTranslatedHeader(CsvReportKeys.FromDate, t),
    },
    {
      key: CsvReportKeys.UntilDate,

      translatedHeader: getTranslatedHeader(CsvReportKeys.UntilDate, t),
    },
    {
      key: CsvReportKeys.WorkingHours,

      translatedHeader: getTranslatedHeader(CsvReportKeys.WorkingHours, t),
    },
    {
      key: CsvReportKeys.ActiveDays,

      translatedHeader: getTranslatedHeader(CsvReportKeys.ActiveDays, t),
    },
    {
      key: CsvReportKeys.CreatedOn,

      translatedHeader: getTranslatedHeader(CsvReportKeys.CreatedOn, t),
    },
    {
      key: CsvReportKeys.CreatedBy,

      translatedHeader: getTranslatedHeader(CsvReportKeys.CreatedBy, t),
    },
    {
      key: CsvReportKeys.Customer,

      translatedHeader: getTranslatedHeader(CsvReportKeys.Customer, t),
    },
    {
      key: CsvReportKeys.Project,

      translatedHeader: getTranslatedHeader(CsvReportKeys.Project, t),
    },
    {
      key: CsvReportKeys.OrganizationName,

      translatedHeader: getTranslatedHeader(CsvReportKeys.OrganizationName, t),
    },
    {
      key: CsvReportKeys.Country,

      translatedHeader: getTranslatedHeader(CsvReportKeys.Country, t),
    },
    {
      key: CsvReportKeys.Status,

      translatedHeader: getTranslatedHeader(CsvReportKeys.Status, t),
    },
    {
      key: CsvReportKeys.IsRecurrent,

      translatedHeader: getTranslatedHeader(CsvReportKeys.IsRecurrent, t),
    },
  ];
};

export const getStructmasterLicenseTableHeaders = (t: TFunction) => {
  return [
    {
      key: IdentityCsvReportKeys.FromDate,
      translatedHeader: getStructmasterTranslatedHeader(IdentityCsvReportKeys.FromDate, t),
    },
    {
      key: IdentityCsvReportKeys.UntilDate,

      translatedHeader: getStructmasterTranslatedHeader(IdentityCsvReportKeys.UntilDate, t),
    },
    {
      key: IdentityCsvReportKeys.ActiveDays,

      translatedHeader: getStructmasterTranslatedHeader(IdentityCsvReportKeys.ActiveDays, t),
    },
    {
      key: IdentityCsvReportKeys.CreatedBy,

      translatedHeader: getStructmasterTranslatedHeader(IdentityCsvReportKeys.CreatedBy, t),
    },
    {
      key: IdentityCsvReportKeys.LicenseName,

      translatedHeader: getStructmasterTranslatedHeader(IdentityCsvReportKeys.LicenseName, t),
    },
    {
      key: IdentityCsvReportKeys.OrganizationName,

      translatedHeader: getStructmasterTranslatedHeader(IdentityCsvReportKeys.OrganizationName, t),
    },
    {
      key: IdentityCsvReportKeys.Country,

      translatedHeader: getStructmasterTranslatedHeader(IdentityCsvReportKeys.Country, t),
    },
    {
      key: IdentityCsvReportKeys.Status,

      translatedHeader: getStructmasterTranslatedHeader(IdentityCsvReportKeys.Status, t),
    },
    {
      key: IdentityCsvReportKeys.ActiveUser,

      translatedHeader: getStructmasterTranslatedHeader(IdentityCsvReportKeys.ActiveUser, t),
    },
    {
      key: IdentityCsvReportKeys.SoftwareVersion,

      translatedHeader: getStructmasterTranslatedHeader(IdentityCsvReportKeys.SoftwareVersion, t),
    },
  ];
};

export const abslouteMaxLicenseDate = new Date(2098, 0, 1);
export const getUnlimitedLicenseAttributes = () => {
  const today = new Date();
  const fromDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const untilDate = new Date(abslouteMaxLicenseDate);
  const workingHours = dateDiffToHours(fromDate, untilDate);
  const maxPeriodInDays = 28000;

  return {
    fromDate,
    untilDate,
    workingHours,
    maxPeriodInDays,
  };
};

export class LicenseDataSummaryStore extends DataStore<LicenseDataSummaryItem> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/api/licenses`, []);
  }
  public async licenseExists(query: Partial<Query>) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<QueryResult<LicenseDataSummaryItem>>(`${this.baseUrl}?${DataStore.buildUrl(query as Query)}`);

      let item = response.data.items.firstOrDefault();
      if (item) {
        item = updateLicenseFormat(item);
      }
      if (response && response.data.count) {
        return item;
      }

      return false;
    });
  }

  public async loadFromOrganizationIds(query: Partial<Query>, ids?: string[]) {
    const item = { ids: ids || [] };
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.put<{ ids: string[] }, QueryResult<LicenseDataSummaryItem>>(
        `${this.baseUrl}/from-organization-ids?${DataStore.buildUrl(query as Query)}`,
        item
      );
      const items = (response.data.items || []).map((v) => updateLicenseFormat(v));
      this._state.set((s) => {
        s.items = items;
        s.count = response.data.count || 0;
        return s;
      });
      return true;
    });
  }

  public async load(query: Partial<Query>) {
    return await this.handleCallAsync(async () => {
      if (!query.tableUpdate) {
        this._state.set((s) => {
          s.items = [];
          s.count = 0;
          return s;
        });
      }
      var response = await this.httpService.get<QueryResult<LicenseDataSummaryItem>>(`${this.baseUrl}?${DataStore.buildUrl(query as Query)}`);
      const items = (response.data.items || []).map((v) => updateLicenseFormat(v));
      this._state.set((s) => {
        s.items = items;
        s.count = response.data.count || 0;
        return s;
      });
      return true;
    });
  }
}
export class LicenseSummaryStore extends FormStore<string, string> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/api/licenses`);
  }
  public async load(id: string) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<string>(`${this.baseUrl}/${encodeURIComponent(id)}/get-license`);
      this._state.set((s) => {
        s.item = response.data as string;
        return s;
      });
      return true;
    });
  }
}

export class LicenseDataItemStore extends FormStore<LicenseDataItem, CreateLicenseDataItem> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/api/licenses`);
  }

  public async load(id: string) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<LicenseDataItem>(`${this.baseUrl}/${encodeURIComponent(id)}`);
      let data = response.data;
      if (isHourBasedLicense(data.deviceType)) {
        data.workingHours = dateDiffToHours(data.fromDate!, data.untilDate!);
        data.fromDate = undefined;
        data.untilDate = undefined;
      }
      data = updateLicenseFormat(data);
      this._state.set((s) => {
        s.item = data;
        return s;
      });
      return true;
    });
  }

  public async create(item: CreateLicenseDataItem) {
    let data = item;
    if (isHourBasedLicense(data.deviceType)) {
      const dates = hoursToDateDiff(data.workingHours!);
      data.workingHours = undefined;
      data.fromDate = dates.from;
      data.untilDate = dates.until;
    }
    const currentLanguage = i18next.language !== Languages.Russian ? i18next.language : 'ru-RU';
    const postData: CreateLicenseDataItem = { ...item, language: currentLanguage };

    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<CreateLicenseDataItem, CommandResult<LicenseDataItem>>(`${this.baseUrl}`, postData);
      return result.data;
    });
  }

  public async update(item: LicenseDataItem) {
    let data = item;
    if (isHourBasedLicense(data.deviceType)) {
      const dates = hoursToDateDiff(data.workingHours!);
      data.workingHours = undefined;
      data.fromDate = dates.from;
      data.untilDate = dates.until;
    }
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.put<LicenseDataItem, CommandResult<LicenseDataItem>>(
        `${this.baseUrl}/${encodeURIComponent(item.id)}/update`,
        data
      );
      return result.data;
    });
  }

  public async updateSignOff(item: { signOff: boolean }, id: string) {
    return await this.handleCallAsync(async () => {
      return await this.httpService.put<{ signOff: boolean }, CommandResult<{ signOff: boolean }>>(
        `${this.baseUrl}/${encodeURIComponent(id)}/signOff`,
        item
      );
    });
  }
}

export class LicenseSuggestionsStore extends FormStore<LicenseSuggestions, LicenseSuggestions> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/api/licenses`);
  }
  public async getSuggestions(request: LicenseSuggestionsRequest) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<LicenseSuggestions>(`${this.baseUrl}/get-suggestions`);
      this._state.set((s) => {
        s.item = response.data as LicenseSuggestions;
        return s;
      });
      return true;
    });
  }
}

export class ReportItemStore extends DataStore<any> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/api/licenses`, []);
  }

  public async get(item: CsvReportRequest) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<any, Blob>(`${this.baseUrl}/get-csv-report`, item, { responseType: 'blob' });

      if (result.data == null || result.data.size === 0) {
        throw Error(i18n.t('CrashError'));
      }

      FileSaver.saveAs(result.data, item.fileName);

      return true;
    });
  }

  public async getFull(query: Query, item: CsvReportRequest) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<any, Blob>(`${this.baseUrl}/get-full-csv-report?${DataStore.buildUrl(query as Query)}`, item, {
        responseType: 'blob',
      });

      if (result.data == null || result.data.size === 0) {
        throw Error(i18n.t('CrashError'));
      }

      FileSaver.saveAs(result.data, item.fileName);

      return true;
    });
  }
}

export class LicenseCreationAttributesStore extends FormStore<LicenseCreationAttributes, NewLicensCreationAttributes> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/api/license-attr`);
  }
  public async post(item: NewLicensCreationAttributes) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<NewLicensCreationAttributes, CommandResult<LicenseCreationAttributes>>(`${this.baseUrl}`, item);
      return result.data;
    });
  }
  public async checkForLicenseAmountReset(item: LicenseScopeRequest) {
    return await this.handleCallAsync(async () => {
      await this.httpService.put(`${this.baseUrl}/update-maxLicensesPerMonth`, item);
      return true;
    });
  }
  public async getByUserScope(item: LicenseScopeRequest) {
    return (
      (await this.handleCallAsync(async () => {
        try {
          var response = await this.httpService.get<LicenseCreationAttributes>(`${this.baseUrl}/get-by-user-scope`, item);
          this._state.set((s) => {
            s.item = response.data as LicenseCreationAttributes;
            return s;
          });
          return true;
        } catch (e) {
          return false;
        }
      })) ?? false
    );
  }
  public async deleteByUserScope(item: LicenseScopeRequest) {
    return await this.handleCallAsync(async () => {
      return await this.httpService.delete<LicenseScopeRequest, LicenseCreationAttributes>(`${this.baseUrl}/delete-by-user-scope`, item);
    });
  }
}

export class StructmasterLicensesStore extends DataStore<StructmasterLicenseSummary> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/structmaster-license`, []);
  }

  public async createUnsignedLicense(item: CreateUnsignedStructmasterLicense) {
    return await this.handleCallAsync(async () => {
      try {
        var result = await this.httpService.post<CreateUnsignedStructmasterLicense, CommandResult<UnsignedStructmasterLicense>>(
          `${this.baseUrl}/unsigned`,
          item
        );
        return result.data;
      } catch (e) {
        return { item: undefined, error: e as string, isSuccess: false, messages: [] };
      }
    });
  }

  public async saveUnsignedLicense(id: string, updatedLicense: UnsignedStructmasterLicense, previousLicense?: UnsignedStructmasterLicense) {
    return await this.handleCallAsync(async () => {
      let result: { item?: UnsignedStructmasterLicense; error?: string; isSuccess: boolean; messages: Message[] } = {
        item: undefined,
        error: undefined,
        isSuccess: true,
        messages: [],
      };
      try {
        const originalMembers = previousLicense?.members.map((v) => v.memberId) ?? [];

        const updateItem: UpdateUnsignedStructmasterLicense = {
          expirationDate: updatedLicense.expirationDate,
          name: updatedLicense.licenseName,
          organizationId: updatedLicense.organizationId,
        };

        let updateLicenseData = updateItem.name !== previousLicense?.licenseName;
        updateLicenseData = updateLicenseData || updateItem.organizationId !== previousLicense?.organizationId;
        updateLicenseData =
          updateLicenseData || moment(updateItem.expirationDate).toISOString() !== moment(previousLicense?.expirationDate).toISOString();

        console.log(moment(updateItem.expirationDate).toISOString());
        console.log(moment(previousLicense?.expirationDate).toISOString());

        if (updateLicenseData)
          result = (
            await this.httpService.put<UpdateUnsignedStructmasterLicense, CommandResult<UnsignedStructmasterLicense>>(
              `${this.baseUrl}/unsigned/${encodeURIComponent(id)}`,
              updateItem
            )
          ).data;

        const membersToAdd = updatedLicense.members
          .filter((updatedMember) => !originalMembers.includes(updatedMember.memberId))
          .map((member) => member.memberId);

        const membersToRemove = originalMembers.filter(
          (originalMemberId) => !updatedLicense.members.some((updatedMember) => updatedMember.memberId === originalMemberId)
        );

        if (membersToRemove.length > 0)
          await this.httpService.delete<{ members: string[] }, CommandResult<UnsignedStructmasterLicense>>(
            `${this.baseUrl}/unsigned/${encodeURIComponent(id)}/users`,
            { members: membersToRemove }
          );

        if (membersToAdd.length > 0)
          await this.httpService.post<{ members: string[] }, CommandResult<UnsignedStructmasterLicense>>(
            `${this.baseUrl}/unsigned/${encodeURIComponent(id)}/users`,
            { members: membersToAdd }
          );

        return result;
      } catch (e) {
        return { item: undefined, error: e as string, isSuccess: false, messages: [] };
      }
    });
  }

  public async loadStructmasterLicenses({ query, ids, userId, unsigned }: StructmasterLicensesQueryFilter) {
    return await this.handleCallAsync(async () => {
      let extendedQuery: Partial<Query> = { ...query, parameters: { userId } };
      var response = await this.httpService.get<QueryResult<StructmasterLicenseSummary>>(
        `${this.baseUrl}/${unsigned ? 'unsigned' : 'get-structmaster-licenses'}?${DataStore.buildUrl((userId ? extendedQuery : query) as Query)}`
      );

      this._state.set((s) => {
        s.items = response.data.items || [];
        s.count = response.data.count || 0;
        return s;
      });

      return true;
    });
  }

  public async getStructmasterLicenses({ query, ids, userId, unsigned }: StructmasterLicensesQueryFilter) {
    return await this.handleCallAsync(async () => {
      let extendedQuery: Partial<Query> = { ...query, parameters: { userId } };
      var response = await this.httpService.get<QueryResult<StructmasterLicenseSummary>>(
        `${this.baseUrl}/${unsigned ? 'unsigned' : 'get-structmaster-licenses'}?${DataStore.buildUrl((userId ? extendedQuery : query) as Query)}`
      );
      return response.data;
    });
  }

  public async getLicense(id: string, unsigned?: boolean) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<UnsignedStructmasterLicense>(`${this.baseUrl}${unsigned ? '/unsigned' : ''}/${id}`);
      return response.data as UnsignedStructmasterLicense;
    });
  }

  public async saveLicense(id: string, item: GroupItem, previousLicense?: StructmasterLicenseItem | UnsignedStructmasterLicense) {
    return await this.handleCallAsync(async () => {
      const originalMembers = previousLicense?.members.map((v) => v.memberId) ?? [];
      const originalName = previousLicense?.licenseName;

      let result: { item?: StructmasterLicenseItem; error?: string; isSuccess: boolean; messages: Message[] } = {
        item: undefined,
        error: undefined,
        isSuccess: false,
        messages: [],
      };

      try {
        if (originalName !== item.name)
          result = (await this.httpService.put<GroupItem, CommandResult<StructmasterLicenseItem>>(`${this.baseUrl}/${encodeURIComponent(id)}`, item))
            .data;

        const membersToAdd = item.members
          .filter((updatedMember) => !originalMembers.includes(updatedMember.memberId))
          .map((member) => member.memberId);

        const membersToRemove = originalMembers.filter(
          (originalMemberId) => !item.members.some((updatedMember) => updatedMember.memberId === originalMemberId)
        );

        if (membersToRemove.length > 0)
          await this.httpService.delete<{ members: string[] }, CommandResult<UnsignedStructmasterLicense>>(
            `${this.baseUrl}/${encodeURIComponent(id)}/users`,
            { members: membersToRemove }
          );

        if (membersToAdd.length > 0)
          await this.httpService.post<{ members: string[] }, CommandResult<UnsignedStructmasterLicense>>(
            `${this.baseUrl}/${encodeURIComponent(id)}/users`,
            { members: membersToAdd }
          );
      } catch (e) {
        return { item: null, error: e as string, isSuccess: false, messages: [] };
      }

      return result;
    });
  }

  public async createLicense(item: CreateStructmasterLicenseItem) {
    return await this.handleCallAsync(async () => {
      try {
        var result = await this.httpService.post<CreateStructmasterLicenseItem, CommandResult<StructmasterLicenseItem>>(`${this.baseUrl}`, item);
        return result.data;
      } catch (e) {
        return { isSuccess: false, error: e as string, identity: '', messages: [] };
      }
    });
  }

  public async signOff(id: string, item: CreateStructmasterLicenseItem) {
    return await this.handleCallAsync(async () => {
      try {
        var result = await this.httpService.post<CreateStructmasterLicenseItem, CommandResult<CreateStructmasterLicenseItem>>(
          `${this.baseUrl}/unsigned/signOff/${id}`,
          item
        );
        return result.data;
      } catch (e) {
        return { item: undefined, error: e as string, isSuccess: false, messages: [] };
      }
    });
  }

  public async getReport(query: Query, item: StructmasterCsvReportRequest) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<any, Blob>(`${this.baseUrl}/get-full-csv-report?${DataStore.buildUrl(query as Query)}`, item, {
        responseType: 'blob',
      });

      if (result.data == null || result.data.size === 0) {
        throw Error(i18n.t('CrashError'));
      }

      FileSaver.saveAs(result.data, item.fileName);

      return true;
    });
  }

  public async unlockLicense(id: string) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.delete<{}, CommandResult<{}>>(`${this.baseUrl}/${id}/unblock-license`, {});
      return result.data;
    });
  }

  public async deleteUnsignedLicense(id: string) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.delete<{}, CommandResult<{}>>(`${this.baseUrl}/unsigned/${id}`, {});
      return result.data;
    });
  }

  public async getUsersAlreadyOnGroupType(organizationId: string, groupType: GroupType, groupId?: string) {
    return await this.handleCallAsync(async () => {
      let response = await this.httpService.post<{ tenantId: string; groupType: GroupType; groupId?: string }, string[]>(
        `${this.baseUrl}/get-forbidden-users`,
        { tenantId: organizationId, groupType, groupId }
      );
      return response.data;
    });
  }
}
